"use client";
import Image from "next/image";
import SectionHeading from "@/components/SectionHeading";
import { useRouter } from "next/navigation";
import { useState } from "react";

const TESTIMONIALS = [
  {
    name: "Adeline Chan",
    quote:
      "Thankful to have worked with such a dedicated and knowledgeable property agent. Winnie guided me patiently through my property progression journey and made the entire process smooth and reassuring. Truly appreciate her professionalism, advice, and commitment throughout.",
  },
  {
    name: "Mary Ho",
    quote:
      "Very thankful for the guidance and advice throughout my property journey. Winnie was patient, sincere, and helped me secure a more profitable property that better suits my needs. Appreciate the professionalism and smooth experience from start to finish.",
  },
  {
    name: "Lily Yang",
    quote:
      "Winnie is highly responsive, logical, and truly prioritizes her client's interests. Instead of lowballing to force a quick commission, she provided constructive advice that maximized my benefits. She successfully sold my property within a month — an incredible feat after my previous agent failed to deliver for over nine months.",
  },
  {
    name: "Tang Yong Siong",
    quote:
      "Winnie Lim is highly knowledgeable and gives upfront, honest advice without any superficial sales pitch. She willingly collaborates with other agents to prioritize the seller's benefits, setting her apart from others. Her dedication shines through as she constantly goes the extra mile to deliver exceptional results.",
  },
  {
    name: "YJ Chua",
    quote:
      "Winnie was able to get the property sold within the expected timeline despite a challenging market environment. What stood out most was her proactive approach — always staying ahead, providing updates, and anticipating issues instead of simply reacting when problems arose.",
  },
  {
    name: "Jo Chee",
    quote:
      "Winnie is honest and transparent throughout the entire process, giving practical advice instead of overpromising. Despite the difficult market conditions, she successfully sold the property above valuation and achieved one of the highest transacted prices in the block, all within the expected timeline. Thank you Winnie.",
  },
  {
    name: "Kumar",
    quote:
      "Winnie was exceptional! She responded swiftly to every request and truly took the time to listen and understand exactly what we were looking for. She went above and beyond by negotiating firmly on our behalf to secure a fair price despite an initially higher quote. We are truly grateful for her dedication and support.",
  },
  {
    name: "Alex Toh",
    quote:
      "Winnie has been a great help to us. She handled our rental entirely — from repairs to sprucing up the space and finding the right tenants, eventually negotiating a good price. We were totally hands off, making it a real passive income. 100% recommend Winnie for all your property needs.",
  },
];

// JSON-LD schema — all 8 reviews, structured for Google & AI crawlers
const REVIEWS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  "name": "Winnie Lim Hui Nee",
  "description": "Licensed CEA real estate agent and founder of AIProperty.sg. Associate Division Director at Huttons Asia Pte. Ltd. CEA Registration No. R061623D.",
  "url": "https://aiproperty.sg/about",
  "telephone": "+6588772688",
  "hasCredential": "CEA Registration No. R061623D",
  // sameAs links Winnie's schema identity to authoritative external sources —
  // a strong trust signal for AI fact-checkers (ChatGPT, Perplexity, Gemini).
  "sameAs": [
    "https://www.cea.gov.sg/aceas/public-register/sales/2",
    "https://www.huttons.com.sg",
  ],
  "worksFor": {
    "@type": "Organization",
    "name": "Huttons Asia Pte. Ltd.",
    "url": "https://www.huttons.com.sg",
  },
  "review": TESTIMONIALS.map((t) => ({
    "@type": "Review",
    "reviewBody": t.quote,
    "datePublished": "2024-01-01",
    "author": {
      "@type": "Person",
      "name": t.name,
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": "5",
      "bestRating": "5",
      "worstRating": "1",
    },
    "itemReviewed": {
      "@type": "RealEstateAgent",
      "name": "Winnie Lim Hui Nee",
      "url": "https://aiproperty.sg/about",
    },
  })),
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5",
    "reviewCount": String(TESTIMONIALS.length),
    "bestRating": "5",
    "worstRating": "1",
  },
};

function TestimonialsCarousel() {
  const [index, setIndex] = useState(0);
  const total = TESTIMONIALS.length;

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  // Visible indices: current, current+1, current+2 (wrapping)
  const visibleSet = new Set([0, 1, 2].map((offset) => (index + offset) % total));

  return (
    <section className="bg-[#F7F9FF] py-16">
      {/*
        JSON-LD: all 8 reviews + aggregate rating.
        Rendered in the DOM on every page load — crawlable by Google and
        AI search engines (Perplexity, ChatGPT, Gemini) regardless of
        which carousel slide is active.
      */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(REVIEWS_SCHEMA) }}
      />

      <div className="container mx-auto md:px-8 px-4">
        <div className="mb-10">
          <SectionHeading>
            What Clients <span className="text-brand-600">Say</span>
          </SectionHeading>
        </div>

        <div className="relative">
          {/*
            ALL 8 testimonials are rendered in the DOM at all times.
            Non-visible cards use `hidden` (display:none) — Google and AI
            crawlers index display:none content, so all 8 reviews are
            discoverable even when only 3 are visually active.
            Semantic <blockquote> + <cite> markup reinforces credibility signals.
          */}
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <blockquote
                key={i}
                aria-hidden={!visibleSet.has(i)}
                className={[
                  "bg-white rounded-2xl p-6 shadow-sm flex flex-col gap-4 m-0",
                  visibleSet.has(i) ? "block" : "hidden",
                ].join(" ")}
              >
                {/* 5-star rating */}
                <div className="flex gap-0.5" aria-label="5 out of 5 stars">
                  {[1,2,3,4,5].map((s) => (
                    <svg key={s} width="18" height="18" viewBox="0 0 24 24" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-[#3A3A3A] text-[15px] leading-[26px] flex-1">
                  {t.quote}
                </p>
                <footer className="border-t border-gray-100 pt-4">
                  <cite className="not-italic text-[#120F30] font-semibold text-[15px]">
                    {t.name}
                  </cite>
                </footer>
              </blockquote>
            ))}
          </div>

          {/* Arrows + dots */}
          <div className="flex items-center justify-center gap-6 mt-8">
            <button
              onClick={prev}
              aria-label="Previous testimonials"
              className="w-10 h-10 rounded-full border-2 border-[#0094FF] text-[#0094FF] hover:bg-[#0094FF] hover:text-white transition-colors flex items-center justify-center"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === index ? "bg-[#0094FF]" : "bg-[#CBD5E1]"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              aria-label="Next testimonials"
              className="w-10 h-10 rounded-full border-2 border-[#0094FF] text-[#0094FF] hover:bg-[#0094FF] hover:text-white transition-colors flex items-center justify-center"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const About = () => {
  const router = useRouter();
  return (
    <div className="md:pt-[100px] pt-10">
      {/* Hero Section */}
      <section className="container mx-auto md:px-8 px-4">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="">
            <div className="text-[#7D7999] text-[20px] font-semibold uppercase tracking-wide mb-4">
              About Us
            </div>
            <h1 className="text-3xl md:text-[36px] text-[#0B0B0B] lg:text-[36px] font-semibold leading-[46px] mb-5">
              Guiding You to Your Dream Home with Advanced AI and Unmatched
              Insight
            </h1>
            <p className="text-[#3A3A3A] text-[18px] leading-[28px] mb-10">
              AI Property is Singapore&apos;s curated platform for new launch
              and select resale properties &mdash; sourced directly from
              developers. We combine comprehensive listings, transparent floor
              plans and pricing, and AI-assisted discovery so you can compare
              your options on data, not hype. When you&apos;re ready to act, a
              licensed CEA agent is one message away.
            </p>
            <button
              className="rounded  bg-[#0094FF] hover:bg-[#0080E0] text-white text-[16px] font-medium px-10 py-3"
              onClick={() => router.push("/contact")}
            >
              Contact Us
            </button>
          </div>
          {/* Real Singapore skyline (same asset as the homepage hero)
              stretched to fill the column — the old fixed-width blob
              graphic left the right half of the hero mostly empty on
              wide desktop viewports. */}
          <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden">
            <Image
              src={"/hero-bg.jpg"}
              alt="Singapore Marina Bay skyline at sunset"
              fill
              priority
              className="object-cover"
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto md:px-8 px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: "Our Mission",
              image: "/bullseye.png",
              description:
                "Make finding your next Singapore home simpler and more transparent. Cut through marketing noise so buyers can compare projects on facts, not hype.",
            },
            {
              title: "Our Vision",
              image: "/vision.png",
              description:
                "To be Singapore's most trusted platform for new launch and resale property discovery — where every buyer has the same access to data that agents and developers do.",
            },
            {
              title: "Our Values",
              image: "/diamond.png",
              description:
                "Transparency, accuracy, and trust. Every listing is sourced directly from developers — no inflated quotes, no paywalls, no hidden agendas.",
            },
            {
              title: "Our Goal",
              image: "/flag-two.png",
              description:
                "Help every buyer make a confident, well-informed property decision — backed by AI-assisted discovery and a real licensed agent when you're ready.",
            },
          ].map((feature, index) => (
            <div
              key={index}
              className="p-6 rounded-2xl border-2 border-[#0094FF] hover:border-[#0070C8] transition-colors"
            >
              <img src={feature.image} alt="" className="mb-4" />
              <h3 className="text-[22px] font-medium text-[#0094FF] mb-3">
                {feature.title}
              </h3>
              <p className="text-[#3A3A3A] text-[18px]">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* "HOW WE WORK" SECTION */}
      <section className="bg-[#E6F4FF] py-16">
        <div className="container mx-auto md:px-8 px-4">
          <div className=" mx-auto">
            <div className="mb-8">
              <SectionHeading>
                How we <span className="text-brand-600">work</span>
              </SectionHeading>
            </div>
            
            {/* Re-formatted from a list to a grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {[
                {
                  title: "Search Smarter",
                  description:
                    "Browse every active new launch and select resale project across all 28 districts. Filter by price, tenure, type, T.O.P or location.",
                },
                {
                  title: "Compare With Confidence",
                  description:
                    "Transparent floor plans, site plans, and pricing — sourced directly from developers, not aggregator middlemen.",
                },
                {
                  title: "AI-Assisted Discovery",
                  description:
                    "Save listings to your collections and let our system surface similar projects you might have missed, based on what you've shortlisted.",
                },
                {
                  title: "Talk to a Real Agent",
                  description:
                    "When you're ready, our licensed CEA agent is one tap away on WhatsApp. Real human, real local expertise — no chatbots, no call centres.",
                },
                {
                  title: "No Pressure, No Cost",
                  description:
                    "Our platform is free for buyers. We're paid by developers when you transact, so the goal is finding the right home — not pushing the most expensive one.",
                },
              ].map((step, index) => (
                <div key={index} className="p-6 bg-white rounded-lg shadow-md">
                  <h3 className="text-[22px] text-[#121212] font-semibold mb-3">
                    {index + 1}. {step.title}
                  </h3>
                  <p className="text-[#3A3A3A] text-[18px] font-normal">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER STORY SECTION */}
      <section id="founder-story" className="container mx-auto md:px-8 px-4 py-16">
        <div className="mb-10">
          <SectionHeading>
            The Story Behind <span className="text-brand-600">AIProperty.sg</span>
          </SectionHeading>
        </div>
        <div className="grid md:grid-cols-3 gap-10 items-start">
          {/* Photo only */}
          <div className="rounded-2xl overflow-hidden">
            <div className="relative w-full aspect-[3/4]">
              <Image
                src="/winnie-lim-headshot.png"
                alt="Winnie Lim Hui Nee — Associate Division Director, Huttons Asia"
                fill
                className="object-cover object-top"
              />
            </div>
          </div>

          {/* Bio story + credentials */}
          <div className="md:col-span-2 flex flex-col gap-6">
            <div className="space-y-8 text-[#3A3A3A] text-[16px] leading-[27px]">
              <div>
                <p className="text-[13px] font-semibold uppercase tracking-widest text-[#0094FF] mb-2">Background</p>
                <p>
                  I came into real estate from the supply chain industry. For several
                  years, my job was to read demand and supply patterns and negotiate
                  prices. Most people do not think of that as property training. I do.
                </p>
              </div>

              <div>
                <p className="text-[13px] font-semibold uppercase tracking-widest text-[#0094FF] mb-2">Through the tough market</p>
                <p>
                  I joined ERA Realty Network in September 2019 and closed a major
                  transaction in my first month. When Covid-19 hit, a lot of agents
                  pulled back. I dug deeper into demand and supply data, tracked how
                  pricing was shifting, and negotiated directly with developers on my
                  clients&apos; behalf. Many of those properties went on to achieve strong
                  capital appreciation.
                </p>
              </div>

              <div>
                <p className="text-[13px] font-semibold uppercase tracking-widest text-[#0094FF] mb-2">Recognition</p>
                <p>
                  In 2024, I received the Million Dollar Award — not because of the
                  recognition, but because of what it represents: years of showing up
                  consistently and putting clients&apos; interests first. In 2025, I moved
                  to Huttons Asia after meeting Rex Tan, who opened my eyes to how much
                  further technology could take this industry.
                </p>
              </div>

              {/* Pull quote */}
              <blockquote className="border-l-4 border-[#0094FF] pl-5 py-1">
                <p className="text-[18px] font-medium text-[#120F30] leading-relaxed italic">
                  &ldquo;I will not tell you what you want to hear. I will tell you what the market is telling me.&rdquo;
                </p>
              </blockquote>

              <div>
                <p className="text-[13px] font-semibold uppercase tracking-widest text-[#0094FF] mb-2">Why AIProperty.sg</p>
                <p>
                  That thinking led me to found AIProperty.sg — a platform built on
                  price transparency, Singapore property market insights, and
                  AI-powered analysis of demand and supply trends. My goal is to give
                  property buyers access to the same quality of market intelligence
                  that professionals use.
                </p>
              </div>
            </div>

            {/* Credentials inline under the bio */}
            <div className="border-t border-gray-200 pt-5">
              <p className="text-[#3A3A3A] text-[14px] mb-1">
                <span className="font-semibold text-[#120F30]">Winnie Lim Hui Nee</span>
                {" "}·{" "}
                <span className="text-[#0094FF]">Associate Division Director</span>
              </p>
              <p className="text-[#6B7280] text-[13px] mb-4">
                CEA Reg No: <span className="font-mono">R061623D</span> · Huttons Asia Pte. Ltd · Licence <span className="font-mono">L3008899K</span>
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://wa.me/6588772688"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded bg-[#0094FF] hover:bg-[#0080E0] text-white text-[14px] font-medium px-5 py-2"
                >
                  WhatsApp +65 8877 2688
                </a>
                <a
                  href="/contact"
                  className="rounded border border-[#0094FF] text-[#0094FF] hover:bg-[#E6F4FF] text-[14px] font-medium px-5 py-2"
                >
                  Send a message
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KEY MILESTONES SECTION */}
      <section className="bg-[#E6F4FF] py-12">
        <div className="container mx-auto md:px-8 px-4">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                year: "October 2025",
                title: "Top 100 Producers — 21st Place",
                description: "Ranked 21st out of Huttons Asia's top producers in my first months at the agency.",
                image: "/award-top100.jpg",
              },
              {
                year: "July 2025",
                title: "Platinum Producer",
                description: "Achieved more than $100K in sales commission in a single month.",
                image: "/award-platinum.jpg",
              },
              {
                year: "February 2026",
                title: "Gold Producer",
                description: "Recognised for achieving a 5-figure sales commission in a month.",
                image: "/award-gold.jpg",
              },
            ].map((milestone, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border-2 border-[#0094FF] overflow-hidden"
              >
                <div className="relative w-full aspect-square">
                  <Image
                    src={milestone.image}
                    alt={milestone.title}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="p-6">
                  <p className="text-[#0094FF] text-sm font-semibold uppercase tracking-wide mb-2">
                    {milestone.year}
                  </p>
                  <h3 className="text-[18px] font-semibold text-[#120F30] mb-2">
                    {milestone.title}
                  </h3>
                  <p className="text-[#3A3A3A] text-[15px] leading-relaxed">
                    {milestone.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <TestimonialsCarousel />

      {/* "MEET YOUR AGENT" SECTION */}
      <section className="container mx-auto md:px-8 px-4 py-16">
        <div className="mx-auto">
          <div className="mb-8">
            <SectionHeading>
              Ready to Talk? <span className="text-brand-600">One message away</span>
            </SectionHeading>
          </div>
          <p className="text-[#3A3A3A] text-[18px] mb-6">
            When you&apos;re ready to act, reach out directly. No call centres, no
            chatbots — just a licensed CEA agent who knows the data.
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
            <a
              href="https://wa.me/6588772688"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded bg-[#0094FF] hover:bg-[#0080E0] text-white font-medium px-8 py-3 text-[16px]"
            >
              WhatsApp +65 8877 2688
            </a>
            <a
              href="/contact"
              className="flex items-center justify-center gap-2 rounded border-2 border-[#0094FF] text-[#0094FF] hover:bg-[#E6F4FF] font-medium px-8 py-3 text-[16px]"
            >
              Send a message
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#0094FF] to-[#00D4FF] text-white">
        <div className="container-fluid mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center justify-between">
            <div className="space-y-6 m-auto px-4">
              <h2 className="text-3xl md:text-[36px] text-white font-semibold leading-[46px]">
                Ready to Find Your New Home?
                <br />
                Let Our AI Guide You!
              </h2>
              {/* --- THIS IS THE FIX --- */}
              <button 
                className="rounded-full bg-[#0094FF] custom-shadow-2 hover:bg-[#0080E0] text-white px-12 py-[14px] font-semibold"
                onClick={() => router.push('/')}
              >
                SEARCH FOR YOUR HOME
              </button>
              {/* --- END OF FIX --- */}
            </div>
            <div className="relative h-[300px] md:h-[400px]">
              <Image
                src={"/city-day.png"}
                alt="City skyline"
                fill
                className=""
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;